import {
  getAgreementDetail,
  getAgreementDetailSupplier,
  getAgreementLogDetail,
  getAgreementLogDetailSupplier,
} from 'api/mall';
import { UserContext } from 'libs/contexts';
import { FeatureEnum, isFeatureActive } from 'libs/features';
import { amI } from 'libs/user';
import { useRouter } from 'next/router';
import { groupBy, isEmpty } from 'rambdax';
import { useContext, useEffect, useState } from 'react';
import { Tag, Toast } from 'react-vant';
import { cls } from 'reactutils';
import AgreementRenovationPrice from './AgreementRenovationPrice';
import AreaTemplateTable from './AreaTemplateTable';
import { AuthSubjectContext } from './AuthSubjectBoundry';
import PayQuestionTip from './PayQuestionTip';
import QuestionIcon from './QuestionIcon';
import SimpleTable from './SimpleTable';

const pricingModelEnum = [
  { value: 1, label: '依据店铺销售价格浮动定价' },
  { value: 2, label: '商品砼，基于信息价定价' },
  { value: 3, label: '钢筋，基于网价计算' },
  { value: 4, label: '商品砼，自定义价格' },
  { value: 5, label: '安装材料计价模板' },
  { value: 6, label: '固定协议价' },
  { value: 7, label: '铝型材料定价模板' },
];

const tradeModeEnums = [
  { value: 1, label: '供货模式' },
  { value: 2, label: '专业分包模式' },
  { value: 5, label: '租赁模式' },
];

const newAccountPeriodTypes = [
  {
    value: 2,
    label: '先款后货。即下单后先全额支付货款，再安排发货。',
    seq: 3,
  },
  {
    value: 1,
    label: '货到付款。即完成收货后基于订单安排付款，无需走结算对账流程。',
    seq: 1,
  },
  {
    value: 3,
    label:
      '预付款。即下单后先按约定比例预付货款，再安排发货；完成收货后基于订单、按实际收货金额结清货款。',
    seq: 4,
  },
  {
    value: 0,
    label: '结算付款。即完成收货后，需要进行结算对账流程，再安排付款。',
    seq: 2,
  },
];
const oldAccountPeriodTypes = [
  { value: 2, label: '先款后货', seq: 3 },
  { value: 1, label: '货到付款', seq: 1 },
  { value: 3, label: '预付款', seq: 4 },
  { value: 0, label: '结算付款', seq: 2 },
];

const accountPeriodTypes = newAccountPeriodTypes;

const afterPaymentConfigPaymentMode = [
  {
    value: 1,
    label: '一次性全额付款',
  },
  {
    value: 2,
    label: '分期付款',
  },
];

const settlementIntervalEnum = [
  {
    value: 1,
    label: '日结',
  },
  {
    value: 2,
    label: '周结',
  },
  {
    value: 3,
    label: '月结',
  },
];

const weekData = [
  {
    label: '周一',
    value: 1,
  },
  {
    label: '周二',
    value: 2,
  },
  {
    label: '周三',
    value: 3,
  },
  {
    label: '周四',
    value: 4,
  },
  {
    label: '周五',
    value: 5,
  },
  {
    label: '周六',
    value: 6,
  },
  {
    label: '周日',
    value: 7,
  },
];

const settlementConfigBillingPeriodList = [
  {
    value: 0,
    label: '立即付款',
  },
  {
    value: 1,
    label: '7天后一次性付款',
  },
  {
    value: 2,
    label: '1个月后一次性付款',
  },
  {
    value: 5,
    label: '2个月后一次性付款',
  },
  {
    value: 3,
    label: '3个月后一次性付款',
  },
  {
    value: 4,
    label: '6个月后一次性付款',
  },
  {
    value: 8,
    label: '4个月后一次性付款',
  },
  {
    value: 9,
    label: '5个月后一次性付款',
  },
  {
    value: 6,
    label: '一次性付款，其他',
  },
  {
    value: 7,
    label: '分期付款',
  },
];

export const templatePriceConfigQuoteMode = [
  { value: 1, label: '除税价格' },
  { value: 2, label: '含税价格' },
];

const discountTypeEnum = [
  {
    value: 1,
    label: '浮动比例',
  },
  {
    value: 2,
    label: '浮动金额',
  },
];

const ProtocolTypeEnum = [
  {
    value: 1,
    label: '代理供货类',
  },
  {
    value: 2,
    label: '采购交易类',
  },
];

const areaDiscountRuleConstant = {
  ALL_CONRUNTY: 1,
  BY_AREA: 2,
};

const areaDiscountRuleEnum = [
  {
    value: areaDiscountRuleConstant.ALL_CONRUNTY,
    label: '全国统一浮动',
  },
  {
    value: areaDiscountRuleConstant.BY_AREA,
    label: '按区域浮动',
  },
];

const findEnum = (collection: any[], value) => {
  const enumObj = collection.find(function (c) {
    return c.value === value;
  });

  return enumObj || {};
};

export const findEnumLabel = (collection: any[], value) => {
  return findEnum(collection, value).label;
};

const findEnumValue = (collection: any[], value) => {
  return findEnum(collection, value).value;
};

export type AgreementRole = 'supplier' | 'purchaser' | 'auto';

export default function AgreementContent({
  agreementId,
  isHistory = false,
  role = 'auto',
  netPriceType,
}: {
  agreementId: string;
  isHistory?: boolean;
  role?: AgreementRole;
  netPriceType?: number | null | undefined;
}) {
  const { catchRejection } = useContext(AuthSubjectContext);
  const router = useRouter();
  const userInfo = useContext(UserContext);
  const isSupplier = role === 'auto' ? amI(userInfo, 'supplier') : role === 'supplier';

  const [previewObj, setPreviewObj] = useState(null);
  const [
    mixStationProvidePumpAttributeConfigListData,
    setMixStationProvidePumpAttributeConfigListData,
  ] = useState([]);
  // 泵送
  const [singleAttributeConfigListData, setSingleAttributeConfigListData] = useState([]);
  // 抗折混凝土价格
  const [doubleAttributeConfigListData, setDoubleAttributeConfigListData] = useState([]);

  // 普通混凝土特殊类型
  const [otherAttributeConfigListData, setOtherAttributeConfigListData] = useState([]);

  // 抗折混凝土特殊类型
  const [
    fractureResistanceOtherAttributeConfigListData,
    setFractureResistanceOtherAttributeConfigListData,
  ] = useState([]);

  useEffect(() => {
    const queryFn = catchRejection(
      isHistory
        ? !isSupplier
          ? getAgreementLogDetail
          : getAgreementLogDetailSupplier
        : !isSupplier
        ? getAgreementDetail
        : getAgreementDetailSupplier,
    );

    setPreviewObj(null);

    queryFn(agreementId).then((res) => {
      if (!res) return;
      const previewObj = res.data;
      setPreviewObj(previewObj);
      if (!previewObj) return;
      if (previewObj.config.pricingModelEnum === 2) {
        setMixStationProvidePumpAttributeConfigListData(
          previewObj?.config?.templatePriceConfig?.mixStationProvidePumpAttributeConfigList || [],
        );

        setSingleAttributeConfigListData(
          previewObj?.config?.templatePriceConfig?.singleAttributeConfigList || [],
        );

        setDoubleAttributeConfigListData(
          previewObj?.config?.templatePriceConfig?.fractureResistanceDiscountConfigList || [],
        );

        setOtherAttributeConfigListData(
          previewObj?.config?.templatePriceConfig?.otherAttributeConfigList || [],
        );
        setFractureResistanceOtherAttributeConfigListData(
          previewObj?.config?.templatePriceConfig?.fractureResistanceOtherAttributeConfigList || [],
        );
      } else if (previewObj.config.pricingModelEnum === 4) {
        setMixStationProvidePumpAttributeConfigListData(
          previewObj?.config?.concreteCustomize?.mixStationProvidePumpAttributeConfigList || [],
        );

        setSingleAttributeConfigListData(
          previewObj?.config?.concreteCustomize?.singleAttributeConfigList || [],
        );

        setDoubleAttributeConfigListData(
          previewObj?.config?.concreteCustomize?.fractureResistanceDiscountConfigList || [],
        );

        setOtherAttributeConfigListData(
          previewObj?.config?.concreteCustomize?.otherAttributeConfigList || [],
        );

        setFractureResistanceOtherAttributeConfigListData(
          previewObj?.config?.concreteCustomize?.fractureResistanceOtherAttributeConfigList || [],
        );
      }
    });
  }, [agreementId, isSupplier, isHistory]);

  const hasBengsong = !!previewObj?.config?.templatePriceConfig?.benchmarkEnumList?.includes(3);
  const hasToushui = !!previewObj?.config?.templatePriceConfig?.benchmarkEnumList?.includes(2);

  const showToast = (message: string) => {
    Toast.info({
      overlay: true,
      closeOnClickOverlay: true,
      message,
      duration: 0,
      overlayStyle: {
        background: 'transparent',
      },
      className: 'agreement-tip',
    });
  };

  //  预付款
  const isPrePay = !!(
    previewObj &&
    previewObj.config &&
    Array.isArray(previewObj.config.accountPeriodEnumList) &&
    previewObj.config.accountPeriodEnumList.indexOf(3) > -1
  );

  // 先款后货
  // const isPayFirst = !!(
  //   previewObj &&
  //   previewObj.config &&
  //   Array.isArray(previewObj.config.accountPeriodEnumList) &&
  //   previewObj.config.accountPeriodEnumList.indexOf(2) > -1
  // );

  // 货到付款
  const isGoodsFirst = !!(
    previewObj &&
    previewObj.config &&
    Array.isArray(previewObj.config.accountPeriodEnumList) &&
    previewObj.config.accountPeriodEnumList.indexOf(1) > -1
  );

  //预付款 尾款结清 分期付款
  const isPrePayMoreTimes = !!(
    previewObj &&
    previewObj.config &&
    previewObj.config.prePaymentConfig &&
    previewObj.config.prePaymentConfig.finalPaymentType === 2
  );

  //预付款 尾款结清 结算支付
  const isPrePaysettlement = !!(
    previewObj &&
    previewObj.config &&
    previewObj.config.prePaymentConfig &&
    previewObj.config.prePaymentConfig.finalPaymentType === 3
  );

  // 货到付款之 分期付款
  const isGoodsFirstMoreTimes = !!(
    previewObj &&
    previewObj.config &&
    previewObj.config.afterPaymentConfig &&
    previewObj.config.afterPaymentConfig.paymentModeEnum === 2
  );

  // 结算付款
  const isCustomPay = !!(
    previewObj &&
    previewObj.config &&
    Array.isArray(previewObj.config.accountPeriodEnumList) &&
    previewObj.config.accountPeriodEnumList.indexOf(0) > -1
  );

  // settlelment 付款账期 分期配置
  const showSettlementInstallment = !!(
    previewObj &&
    previewObj.config &&
    previewObj.config.settlementConfig &&
    Array.isArray(previewObj.config.settlementConfig.billingPeriodEnumList) &&
    previewObj.config.settlementConfig.billingPeriodEnumList.indexOf(7) > -1
  );

  // 浮动定价 vs 网价 , true : 依据店铺价格浮动定价
  const isBaseOnSellPrice = !!(
    previewObj &&
    previewObj.config &&
    previewObj.config.pricingModelEnum &&
    previewObj.config.pricingModelEnum === pricingModelEnum[0].value
  );

  // 协议基价
  const isBasePriceConfig = !!(
    previewObj &&
    previewObj.config &&
    previewObj.config.pricingModelEnum &&
    previewObj.config.pricingModelEnum === pricingModelEnum[5].value
  );

  // 商品砼基于信息价定价
  const isConcrete = !!(
    previewObj &&
    previewObj.config &&
    previewObj.config.pricingModelEnum &&
    previewObj.config.pricingModelEnum === pricingModelEnum[1].value
  );

  // 钢筋，基于网价计算, true: 依据店铺价格浮动定价
  const isSteelBarNetPrice = !!(
    previewObj &&
    previewObj.config &&
    previewObj.config.pricingModelEnum &&
    previewObj.config.pricingModelEnum === pricingModelEnum[2].value
  );

  // 商品砼，自定义价格
  const isConcreteCustomize = !!(
    previewObj &&
    previewObj.config &&
    previewObj.config.pricingModelEnum &&
    previewObj.config.pricingModelEnum === pricingModelEnum[3].value
  );

  // 装修材料网价
  const isRenovation = !!(
    previewObj &&
    previewObj.config &&
    previewObj.config.pricingModelEnum &&
    previewObj.config.pricingModelEnum === pricingModelEnum[4].value
  );

  // 铝型材料定价模版
  const isAluminum = !!(previewObj?.config?.pricingModelEnum === pricingModelEnum[6].value);

  // 其他账期天数
  const billingPeriodOtherDays = previewObj?.config?.settlementConfig?.billingPeriodOtherDays;

  const sortPeriod = (collection: any[]) => {
    const local = [...collection];
    try {
      local.sort((a, b) => {
        return findEnum(accountPeriodTypes, a).seq - findEnum(accountPeriodTypes, b).seq;
      });
    } catch (error) {
      console.log('error', error);
    }
    return local;
  };

  // const getPayPeriodText = () => {
  //   const textList = previewObj.config.settlementConfig.billingPeriodEnumList.map(function (val) {
  //     if (val === 6) {
  //       return billingPeriodOtherDays + '天';
  //     }
  //     return findEnumLabel(settlementConfigBillingPeriodList, val);
  //   });
  //   return textList.join(', ');
  // };

  const getPayPeriodTextNew = () => {
    const textList = previewObj.config.settlementConfig.billingPeriodEnumList.map(function (val) {
      if (val === 6 && !!billingPeriodOtherDays) {
        return `${billingPeriodOtherDays}天后一次性付款`;
      }
      return findEnumLabel(settlementConfigBillingPeriodList, val);
    });
    return (
      <>
        {textList.map((txt, i) => (
          <div key={i}>
            {getCircleNum(i + 1)} {txt}
          </div>
        ))}
      </>
    );
  };

  const getCircleNum = (n: number) => {
    if (n < 0 || n > 20) {
      return n;
    } else {
      return String.fromCharCode(9312 + n - 1);
    }
  };

  // 交易方式浮动调价
  const tradeModePriceRenderInstruct = [
    {
      key: 'accountPeriod',
      label: '交易方式',
      width: '28.5%',
      renderFn: function (data) {
        var val = findEnumValue(accountPeriodTypes, data.accountPeriodEnum);
        if (val !== 0) {
          return findEnumLabel(oldAccountPeriodTypes, data.accountPeriodEnum);
        } else {
          if (data.billingPeriodEnum === 6 && !!billingPeriodOtherDays) {
            return `${billingPeriodOtherDays}天后一次性付款`;
          } else {
            var billingPeriod = findEnumLabel(
              settlementConfigBillingPeriodList,
              data.billingPeriodEnum,
            );
            return billingPeriod + '';
          }
        }
      },
    },
    {
      key: 'discount',
      label: '调整比例 （%）',
      width: '34.6%',
      renderFn: function (data) {
        return data.discount;
      },
    },
    {
      key: 'organizationName',
      label: '价格公式',
      width: '36.9%',
      tip: {
        show: true,
        content: ['价格 = 商品销售价格 * (1 + 调整比例%) ', '最终价格将对计算结果进行四舍五入处理'],
      },
      renderFn: function (data) {
        var val = findEnumValue(accountPeriodTypes, data.accountPeriod);
        if (data.discount === 0) {
          return '商品销售价';
        } else {
          var discount =
            data.discount > 0
              ? '1 + ' + +data.discount + '%'
              : '1 - ' + Math.abs(+data.discount) + '%';
          return '商品销售价 * ' + '(' + discount + ')';
        }
      },
    },
  ];

  // 交易方式浮动调价
  const tradeModePriceRenderInstructConcrete = [
    {
      key: 'accountPeriod',
      label: '交易方式',
      width: '98px',
      renderFn: function (data) {
        var val = findEnumValue(accountPeriodTypes, data.accountPeriodEnum);
        if (val !== 0) {
          return findEnumLabel(oldAccountPeriodTypes, data.accountPeriodEnum);
        } else {
          if (data.billingPeriodEnum === 6 && !!billingPeriodOtherDays) {
            return `${billingPeriodOtherDays}天后一次性付款`;
          } else {
            var billingPeriod = findEnumLabel(
              settlementConfigBillingPeriodList,
              data.billingPeriodEnum,
            );
            return billingPeriod + '';
          }
        }
      },
    },
    {
      key: 'discount',
      label: '调整比例 （%）',
      width: '98px',
      renderFn: function (data) {
        return data.discount;
      },
    },
    {
      key: 'organizationName',
      label: '价格公式',
      width: '100px',
      tip: {
        show: true,
        content: ['价格 = 单价 * (1 + 调整比例%) ', '最终价格将对计算结果进行四舍五入处理'],
      },
      renderFn: function (data) {
        if (data.discount === 0) {
          return '单价';
        } else {
          var discount =
            data.discount > 0
              ? '1 + ' + +data.discount + '%'
              : '1 - ' + Math.abs(+data.discount) + '%';
          return '单价 * ' + '(' + discount + ')';
        }
      },
    },
  ];

  // 抗折价格
  const doubleAttributeConfigListRenderInstruct = [
    {
      key: 'kangzhe',
      label: '货品名称',
      width: '75px',
      renderFn: function () {
        return '抗折商品砼';
      },
    },
    {
      key: 'discount',
      label: '强度等级',
      width: '200px',
      renderFn: function (data) {
        return data.attributeValueList.join(', ');
      },
    },
    {
      key: 'normalAttributeValue',
      label: '基价-普通混凝土',
      width: '120px',
      renderFn: function (data) {
        return data.normalAttributeValue;
      },
    },
    {
      key: 'discount',
      label: '浮动加（减）价',
      width: '120px',
    },
    {
      key: 'jiaozhufangshi',
      label: '单价计价公式',
      renderFn: function (data) {
        var val =
          data.discount === 0
            ? ''
            : data.discount > 0
            ? ' + ' + data.discount
            : ' - ' + Math.abs(data.discount);
        return data.normalAttributeValue + '价格' + val;
      },
    },
  ];

  const mixingStationRenderInstruct = [
    {
      key: 'supplierName',
      label: '供应商',
      width: '50%',
      renderFn: function (data) {
        return data.supplierName;
      },
    },
    {
      key: 'stationNameList',
      label: '搅拌站',
      width: '50%',
      renderFn: function (data) {
        return isEmpty(data.stationNameList) ? '--' : data.stationNameList.join('、');
      },
    },
  ];

  const normalRenderinstruction = [
    {
      key: 'attributeSysNo',
      label: '货品名称',
      width: '75px',
      renderFn: function () {
        return '普通商品砼';
      },
    },
    {
      key: 'attributeSysNo',
      label: '强度等级',
      width: '200px',
      renderFn: function (data) {
        return data.attributeValueList.join(', ');
      },
    },
    {
      key: 'net',
      label: '信息价来源',
      width: '75px',
      renderFn: function () {
        return '信息价';
      },
    },
    {
      key: 'discountType',
      label: '浮动类型',
      width: '65px',
      renderFn: function (data) {
        return findEnumLabel(discountTypeEnum, data.discountTypeEnum);
      },
    },
    {
      key: 'discount',
      label: '浮动值',
      width: '70px',
      renderFn: function (data) {
        if (data.discountType === discountTypeEnum[0].value) {
          return data.discount + '%';
        } else {
          return data.discount;
        }
      },
    },
    {
      key: 'discountType',
      label: '单价计价公式',
      renderFn: function (data) {
        if (data.discountTypeEnum === discountTypeEnum[0].value) {
          var discount =
            data.discount > 0
              ? '1 + ' + +data.discount + '%'
              : '1 - ' + Math.abs(+data.discount) + '%';
          return '信息价 * ' + '(' + discount + ')';
        } else {
          var val =
            data.discount === 0
              ? '信息价'
              : data.discount > 0
              ? ' + ' + data.discount
              : ' - ' + Math.abs(data.discount);
          return '信息价' + val;
        }
      },
    },
  ];

  const normalCustomizeRenderinstruction = [
    {
      key: 'attributeSysNo',
      label: '货品名称',
      width: '75px',
      renderFn: function () {
        return '普通商品砼';
      },
    },
    {
      key: 'attributeSysNo',
      label: '强度等级',
      width: '180px',
      renderFn: function (data) {
        return data.attributeValue;
      },
    },
    {
      key: 'price',
      label: '价格',
      // width: "50px",
      renderFn: function (data) {
        return data.price;
      },
    },
  ];

  const mixStationProvidePumpAttributeConfigListRenderInstruct = [
    {
      key: 'category',
      label: '分类',
      width: '85px',
      renderFn: function (data) {
        return data.categoryName;
      },
    },
    {
      key: 'providePumb',
      label: '是否提供厂站泵',
      width: '85px',
      renderFn: function (data) {
        return data.attributeValue;
      },
    },
    {
      key: 'discount',
      label: '浮动加（减）价',
      width: '98px',
    },
    {
      key: 'jiaozhufangshi',
      label: '单价计价公式',
      width: '98px',
      renderFn: function (data) {
        const isBengsong =
          previewObj?.config?.templatePriceConfig?.bengsongAttributeValueSysNoList?.find(
            (v) => data.attributeValueSysNo === v,
          );
        const showText = hasBengsong && isBengsong ? '泵送混凝土价格' : '普通混凝土价格';
        var val =
          data.discount === 0
            ? ''
            : data.discount > 0
            ? ' + ' + data.discount
            : ' - ' + Math.abs(data.discount);
        return showText + val;
      },
    },
  ];

  const singleAttributeConfigListRenderInstruct = [
    {
      key: 'category',
      label: '分类',
      width: '85px',
      renderFn: function (data) {
        return data.categoryName;
      },
    },
    {
      key: 'jiaozhufangshi',
      label: '浇筑方式',
      width: '85px',
      renderFn: function (data) {
        return data.attributeValue;
      },
    },
    {
      key: 'discount',
      label: '浮动加（减）价',
      width: '98px',
    },
    {
      key: 'jiaozhufangshi',
      label: '单价计价公式',
      width: '98px',
      renderFn: function (data) {
        const isBengsong =
          previewObj?.config?.templatePriceConfig?.bengsongAttributeValueSysNoList?.find(
            (v) => data.attributeValueSysNo === v,
          );
        const showText = hasBengsong && isBengsong ? '泵送混凝土价格' : '普通混凝土价格';
        var val =
          data.discount === 0
            ? ''
            : data.discount > 0
            ? ' + ' + data.discount
            : ' - ' + Math.abs(data.discount);
        return showText + val;
      },
    },
  ];

  const otherAttributeConfigListRenderInstruct = [
    {
      key: 'attributeName',
      label: '属性名',
      width: '85px',
      renderFn: function (data) {
        return data.attribute;
      },
    },
    {
      key: 'attributes',
      label: '属性值',
      width: '85px',
      renderFn: function (data) {
        return data.attributeValue;
      },
    },
    {
      key: 'discount',
      label: '浮动加（减）价',
      width: '98px',
      renderFn: function (data) {
        return data.discount;
      },
    },
    {
      key: 'jiaozhufangshi',
      label: '单价计价公式',
      width: '98px',
      renderFn: function (data) {
        const isToushui =
          data.attributeValueSysNo ===
          previewObj?.config?.templatePriceConfig?.toushuiAttributeValueSysNo;
        const showText = hasToushui && isToushui ? '透水混凝土价格' : '普通混凝土价格';
        var val =
          data.discount === 0
            ? ''
            : data.discount > 0
            ? ' + ' + data.discount
            : ' - ' + Math.abs(data.discount);
        return showText + val;
      },
    },
  ];

  const fractureResistanceOtherAttributeConfigListRenderInstruct = [
    {
      key: 'attributeName',
      label: '属性名',
      width: '85px',
      renderFn: function (data) {
        return data.attribute;
      },
    },
    {
      key: 'attributes',
      label: '属性值',
      width: '85px',
      renderFn: function (data) {
        return data.attributeValue;
      },
    },
    {
      key: 'discount',
      label: '浮动加（减）价',
      width: '98px',
      renderFn: function (data) {
        return data.discount;
      },
    },
    {
      key: 'jiaozhufangshi',
      label: '单价计价公式',
      width: '98px',
      renderFn: function (data) {
        const isToushui =
          data.attributeValueSysNo ===
          previewObj?.config?.templatePriceConfig?.toushuiAttributeValueSysNo;
        const showText = hasToushui && isToushui ? '透水混凝土价格' : '抗折混凝土协议价格';
        var val =
          data.discount === 0
            ? ''
            : data.discount > 0
            ? ' + ' + data.discount
            : ' - ' + Math.abs(data.discount);
        return showText + val;
      },
    },
  ];

  const categoryDiscountListRenderInstruct = [
    {
      key: 'categoryName',
      label: '类别',
      width: '130px',
      renderFn: function (data) {
        return data.categoryName;
      },
    },
    {
      key: 'formula',
      label: '单价计价公式类型',
      width: '200px',
      renderFn: function (data) {
        return data.formula;
      },
    },
    {
      key: 'formulaString',
      label: '单价计价公式',
      width: '270px',
      renderFn: function (data) {
        return data.formulaString;
      },
    },
  ];

  const breedDiscountListRenderInstruct = [
    {
      key: 'indexName',
      label: '品种',
      width: '130px',
      renderFn: function (data) {
        return data.indexName;
      },
    },
    {
      key: 'formula',
      label: '单价计价公式类型',
      width: '200px',
      renderFn: function (data) {
        return data.formula;
      },
    },
    {
      key: 'formulaString',
      label: '单价计价公式',
      width: '270px',
      renderFn: function (data) {
        return data.formulaString;
      },
    },
  ];

  const brandDiscountListRenderInstruct = [
    {
      key: 'brandName',
      label: '品牌',
      width: '200px',
      renderFn: function (data) {
        return data.brandList?.map((item) => item.name)?.join('、');
      },
    },
    {
      key: 'formula',
      label: '单价计价公式类型',
      width: '200px',
      renderFn: function (data) {
        return data.formula;
      },
    },
    {
      key: 'formulaString',
      label: '单价计价公式',
      width: '272px',
      renderFn: function (data) {
        return data.formulaString;
      },
    },
  ];

  const modelDiscountListRenderInstruct = [
    {
      key: 'categoryName',
      label: '类别',
      width: '130px',
      renderFn: function (data) {
        return data.categoryName;
      },
    },
    {
      key: 'attribute',
      label: '属性',
      width: '100px',
      renderFn: function (data) {
        return data.attribute;
      },
    },
    {
      key: 'attributeValue',
      label: '属性值',
      width: '120px',
      renderFn: function (data) {
        return data.attributeValue;
      },
    },
    {
      key: 'discountType',
      label: '浮动加（减）价',
      width: '160px',
      renderFn: function (data) {
        var unit = '';
        var typeStr = '';
        if (data.discountTypeEnum === discountTypeEnum[0].value) {
          unit = '%';
          typeStr = discountTypeEnum[0].label;
        } else {
          unit = '元';
          typeStr = discountTypeEnum[1].label;
        }
        return typeStr + data.discount + unit;
      },
    },
    {
      key: 'formulaString',
      label: '单价计价公式',
      width: '160px',
      renderFn: function (data) {
        return data.formulaString;
      },
    },
  ];

  const unloadingDiscountListRenderInstruct = [
    {
      key: 'attributeValue',
      label: '条件',
      width: '130px',
      renderFn: function (data) {
        return data.attributeValue;
      },
    },
    {
      key: 'discount',
      label: '浮动加（减）价',
      width: '100px',
      renderFn: function (data) {
        return data.discount;
      },
    },
    {
      key: 'formulaString',
      label: '单价计价公式',
      // width: "272px",
      renderFn: function (data) {
        return data.formulaString;
      },
    },
  ];

  const transportDistanceDiscountRenderInstruct = [
    {
      key: 'price',
      label: '条件',
      width: '130px',
      renderFn: function (data) {
        return '按照距离计价，距离为用户下单时活动填写';
      },
    },
    {
      key: 'price',
      label: '单价',
      width: '100px',
      renderFn: function (data) {
        return data.price + '元/km·t';
      },
    },
    {
      key: 'formulaString',
      label: '单价计价公式',
      // width: "100px",
      renderFn: function (data) {
        return data.formulaString;
      },
    },
  ];

  const thirdBrandListRenderInstruct = (labelName) => {
    return [
      {
        key: 'name',
        label: labelName,
        renderFn: function (data) {
          return data;
        },
      },
    ];
  };

  const ruleListRenderInstruct = (labelName) => {
    return [
      {
        key: 'name',
        label: labelName + '品牌',
        renderFn: function (data) {
          return data.thirdBrandName;
        },
      },
    ];
  };

  const priceParamListRenderInstruct = (labelName) => {
    return [
      {
        key: 'name',
        label: labelName + '指定规格',
        width: '200px',
        renderFn: function (data) {
          return data.breedName + '/' + data.materialQuantity + '/' + data.model;
        },
      },
      {
        key: 'sort',
        label: '优先级',
        renderFn: function (_data, index) {
          return index + 1;
        },
      },
    ];
  };

  const anchorRulesRenderInstructFn = (row) => {
    let baseColumns = [
      {
        key: 'breedCode',
        label: '锚定品种',
        width: '100px',
        renderFn: function (data) {
          return data?.breedAnchor?.breedName;
        },
      },
      {
        key: 'materialCategorySysNo',
        label: '材料库类别',
        width: '100px',
        renderFn: function (data) {
          return data?.breedAnchor?.materialCategoryName;
        },
      },
    ];

    let materialQuantityColumns = [
      {
        key: 'materialQuantityCode',
        label: '锚定材质',
        width: '100px',
        className: 'border-left',
        renderFn: function (data) {
          return data?.materialQuantityAnchor?.materialQuantityName;
        },
      },
      {
        key: 'materialQuantityAnchor_attributeList',
        label: '材料库属性',
        width: '150px',
        renderFn: function (data) {
          return data?.materialQuantityAnchor?.attributeList?.map((item) => (
            <Tag key={item.attributeSysNo} size="mini">
              {item?.attributeName}
            </Tag>
          ));
        },
      },
    ];

    row?.materialQuantityAnchor?.attributeList?.forEach((attribute) => {
      let dynamicColumn = {
        key: attribute.attributeValueSysNo,
        label: attribute.attributeName,
        width: '100px',
        renderFn: () => attribute.attributeValueName,
      } as any;
      materialQuantityColumns.push(dynamicColumn);
    });

    let modelAnchorColumns = [
      {
        key: 'modelCode',
        label: '锚定规格',
        width: '100px',
        className: 'border-left',
        renderFn: function (data) {
          return data?.modelAnchor?.modelName;
        },
      },
      {
        key: 'modelAnchor_attributeList',
        label: '材料库属性',
        width: '150px',
        renderFn: function (data) {
          return data?.modelAnchor?.attributeList?.map((item) => (
            <Tag key={item.attributeSysNo} size="mini">
              {item?.attributeName}
            </Tag>
          ));
        },
      },
    ];

    row?.modelAnchor?.attributeList?.forEach((attribute) => {
      let dynamicColumn = {
        key: attribute.attributeValueSysNo,
        label: attribute.attributeName,
        width: '100px',
        renderFn: () => attribute.attributeValueName,
      } as any;
      modelAnchorColumns.push(dynamicColumn);
    });

    return [...baseColumns, ...materialQuantityColumns, ...modelAnchorColumns];
  };

  const anchoredCityListRenderInstruct = [
    {
      key: 'city',
      label: '城市',
      width: '100px',
      renderFn: function (data) {
        return data.cityName;
      },
    },
    {
      key: 'sort',
      label: '锚定城市(参考该城市网价)',
      renderFn: function (data) {
        return data.anchoredCityName;
      },
    },
  ];

  const anchoredBrandConfigListRenderInstruct = (labelName) => {
    return [
      {
        key: 'name',
        label: labelName + '品牌',
        renderFn: function (data) {
          return data.netBrand?.name;
        },
      },
    ];
  };

  const baseConcreteRenderInstructfunction = [
    {
      key: 'attributes',
      label: '品类',
      renderFn: function (data) {
        if (data === 1) {
          return '普通商品砼';
        } else if (data === 2) {
          return '透水商品砼';
        } else if (data === 3) {
          return '泵送商品砼';
        }
      },
    },
  ];

  // 泵送普通混凝土价格
  const bengsongValues =
    Object.values(
      groupBy(
        (x: any) => x.attributeSysNo,
        previewObj?.config?.templatePriceConfig?.bengsongAttributeConfigList || [],
      ),
    )?.[0] || [];

  const bengsongRenderinstruction = [
    {
      key: 'attributeSysNo',
      label: '货品名称',
      width: '120px',
      renderFn: function () {
        return '泵送商品砼';
      },
    },
    {
      key: 'attributeSysNo',
      label: '强度等级',
      width: '300px',
      renderFn: function (data) {
        return data.attributeValueList.join(', ');
      },
    },
    {
      key: 'attributeSysNo1',
      label: '浇筑方式',
      width: '100px',
      renderFn: function (data) {
        return '泵送';
      },
    },
    {
      key: 'net',
      label: '信息价来源',
      width: '120px',
      renderFn: function () {
        return '信息价';
      },
    },
    {
      key: 'discountType',
      label: '浮动类型',
      width: '100px',
      renderFn: function (data) {
        return findEnumLabel(discountTypeEnum, data.discountTypeEnum);
      },
    },
    {
      key: 'discount',
      label: '浮动值',
      width: '100px',
      renderFn: function (data) {
        if (data.discountType === discountTypeEnum[0].value) {
          return data.discount + '%';
        } else {
          return data.discount;
        }
      },
    },
    {
      key: 'discountType',
      label: '单价计价公式',
      renderFn: function (data) {
        if (data.discountTypeEnum === discountTypeEnum[0].value) {
          var discount =
            data.discount > 0
              ? '1 + ' + +data.discount + '%'
              : '1 - ' + Math.abs(+data.discount) + '%';
          return '信息价 * ' + '(' + discount + ')';
        } else {
          var val =
            data.discount === 0
              ? '信息价'
              : data.discount > 0
              ? ' + ' + data.discount
              : ' - ' + Math.abs(data.discount);
          return '信息价' + val;
        }
      },
    },
  ];

  // 透水普通混凝土价格
  const toushuiValues =
    Object.values(
      groupBy(
        (x: any) => x.attributeSysNo,
        previewObj?.config?.templatePriceConfig?.toushuiAttributeConfigList || [],
      ),
    )?.[0] || [];

  const toushuiRenderinstruction = [
    {
      key: 'attributeSysNo',
      label: '货品名称',
      width: '120px',
      renderFn: function () {
        return '透水商品砼';
      },
    },
    {
      key: 'attributeSysNo',
      label: '强度等级',
      width: '300px',
      renderFn: function (data) {
        return data.attributeValueList.join(', ');
      },
    },
    {
      key: 'attributeSysNo1',
      label: '特殊类型',
      width: '100px',
      renderFn: function (data) {
        return '透水';
      },
    },
    {
      key: 'net',
      label: '信息价来源',
      width: '120px',
      renderFn: function () {
        return '信息价';
      },
    },
    {
      key: 'discountType',
      label: '浮动类型',
      width: '100px',
      renderFn: function (data) {
        return findEnumLabel(discountTypeEnum, data.discountTypeEnum);
      },
    },
    {
      key: 'discount',
      label: '浮动值',
      width: '100px',
      renderFn: function (data) {
        if (data.discountType === discountTypeEnum[0].value) {
          return data.discount + '%';
        } else {
          return data.discount;
        }
      },
    },
    {
      key: 'discountType',
      label: '单价计价公式',
      renderFn: function (data) {
        if (data.discountTypeEnum === discountTypeEnum[0].value) {
          var discount =
            data.discount > 0
              ? '1 + ' + +data.discount + '%'
              : '1 - ' + Math.abs(+data.discount) + '%';
          return '信息价 * ' + '(' + discount + ')';
        } else {
          var val =
            data.discount === 0
              ? '信息价'
              : data.discount > 0
              ? ' + ' + data.discount
              : ' - ' + Math.abs(data.discount);
          return '信息价' + val;
        }
      },
    },
  ];

  const receivingConfirmationEnum = [
    { value: 1, label: '采购商直接完成收货，无需供应商确认' },
    { value: 2, label: '需要供应商确认收货单' },
  ];

  const onClickTableTip = (tip) => {
    showToast(tip.content.map((l) => '• ' + l).join('\n'));
  };

  const showAccountPeriodToast = (pType) => {
    console.log(pType);
    const type =
      {
        1: 2,
        2: 3,
        3: 7,
      }[pType] || 1;

    const typeTxt = {
      1:
        previewObj.config.tradeModeEnum === 1
          ? `采购企业下单时选择结算付款，供应商接收到订单，即可安排发货。在采购企业确认收货后，双方基于 收货单 进行结算、付款。`
          : `采购企业下单时选择结算付款，供应商按订单履约。双方基于订单进行结算、付款。`,
      2: `采购企业下单时选择货到付款。供应商接收到订单，即可安排发货。采购企业收货验收，在订单完成后按收货金额结清货款。此种交易方式无结算对账流程。`,
      3: `采购企业下单选择先款后货，提交订单后直接支付全额货款给供应商。供应商将在收到货款后安排发货，采购商收货，完成交易。`,
      7: `采购企业下单选择预付款，提交订单后按约定预付货款，供应商收到预付货款后安排发货。采购商收货，交易完成后按收货金额结清剩余货款。`,
    };

    const txt = typeTxt[type];
    showToast(txt);
  };

  const hasAccountPeriod = (index) => {
    return !!previewObj?.config?.accountPeriodEnumList?.includes(accountPeriodTypes[index].value);
  };

  const billingPeriodTypes = [
    { value: 0, label: '立即创建支付单' },
    { value: 1, label: '7天后创建支付单' },
    { value: 2, label: '1个月后创建支付单' },
    { value: 5, label: '2个月后创建支付单' },
    { value: 3, label: '3个月后创建支付单' },
    { value: 8, label: '4个月后创建支付单' },
    { value: 9, label: '5个月后创建支付单' },
    { value: 4, label: '6个月后创建支付单' },
    { value: 6, label: '其他' },
    { value: 7, label: '分期创建支付单' },
  ];

  const weekList = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];

  const getPrePaysettlement = (data: any) => {
    const { finalSettlementConfig } = data;

    // 1: 日结 2: 周结 3: 月结
    if (finalSettlementConfig?.settlementIntervalEnum === 1) {
      return `收货后，(T+${finalSettlementConfig?.settlementAfterDays})日结算`;
    } else if (finalSettlementConfig?.settlementIntervalEnum === 2) {
      return `交易周期为自然周，每周固定结算日为${
        weekList[finalSettlementConfig?.settlementWeekDays - 1]
      }`;
    } else {
      return `交易周期为${finalSettlementConfig?.settlementMonthType}，每月固定结算日为${finalSettlementConfig?.settlementMonthDays}日`;
    }
  };

  const getPaymentOrder = (type: any, data: any, param: any) => {
    const { finalSettlementConfig } = param;
    if (type.includes(6) || type.includes(7)) {
      if (type.includes(6)) {
        return <div>{`其他，${finalSettlementConfig?.billingPeriodOtherDays}天后创建支付单`}</div>;
      } else {
        return (
          <div>
            <div>{data.find((item: any) => type.includes(item.value)).label}</div>
            <div>
              {finalSettlementConfig?.installmentList?.map((item: any, index: any) => (
                <div key={index}>
                  {`第${index + 1}期：结算后T+${item.deferredDays}日内，支付金额为结算金额的${
                    item.ratio
                  }%。`}
                  {item.notFinancialPay && (
                    <span className="installment_item_red">
                      （该笔为非供应链金融支付，不占用额度）
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      }
    } else {
      console.log(data.find((item: any) => type.includes(item.value)).label, 9999);
      return data.find((item: any) => type.includes(item.value)).label;
    }
  };

  return (
    <>
      <div className="agreement-wrap">
        {previewObj && (
          <div>
            <div className="agreement-title">{previewObj.name}</div>
            <Section title="适用范围">
              <SectionRow title="适用组织:">
                {previewObj?.organizationNameList &&
                  previewObj.organizationNameList
                    .split(',')
                    .map((org, i) => <div key={i}>{org}</div>)}
              </SectionRow>
              <SectionRow title="适用供应商:">
                {previewObj?.supplierNameList &&
                  previewObj.supplierNameList.split(',').map((org, i) => <div key={i}>{org}</div>)}
              </SectionRow>
              <SectionRow title="适用项目:">
                {previewObj?.projectNameList && previewObj.projectNameList === '全部项目' ? (
                  <div>全部项目</div>
                ) : (
                  previewObj?.projectNameList?.split(',').map((org, i) => <div key={i}>{org}</div>)
                )}
              </SectionRow>
              <SectionRow title="规则类型:" bordered={false}>
                {findEnumLabel(ProtocolTypeEnum, previewObj.scene)}
              </SectionRow>
            </Section>
            <Section title="交易设置">
              <SectionRow title="交易模式:">
                {findEnumLabel(tradeModeEnums, previewObj.config.tradeModeEnum)}
                <QuestionIcon
                  onClick={() => {
                    showToast(
                      previewObj.config.tradeModeEnum === 1
                        ? `• 用于各种物资品类的交易；
• 提交订单后需进行发货、收货等操作；
• 支持4种交易方式。`
                        : `• 适用于专业分包的交易；
• 提交订单后无需进行收货操作，基于订单进行结算；
• 仅支持“结算付款”的交易方式。`,
                    );
                  }}
                  title={
                    previewObj.config.tradeModeEnum === 1 ? (
                      <>
                        <div>• 用于各种物资品类的交易；</div>
                        <div>• 提交订单后需进行发货、收货等操作；</div>
                        <div>• 支持4种交易方式。</div>
                      </>
                    ) : (
                      <>
                        <div>• 适用于专业分包的交易；</div>
                        <div>• 提交订单后无需进行收货操作，基于订单进行结算；</div>
                        <div>• 仅支持“结算付款”的交易方式。</div>
                      </>
                    )
                  }
                />
              </SectionRow>
              <SectionRow title="交易方式:">
                <div
                  className="account-period-div"
                  style={{
                    flexDirection: 'column',
                  }}
                >
                  {sortPeriod(previewObj.config.accountPeriodEnumList).map((pType, i) => (
                    <div key={i} className="account-period-item">
                      {getCircleNum(i + 1)}
                      {findEnumLabel(accountPeriodTypes, pType)}
                      <QuestionIcon
                        onClick={() => {
                          showAccountPeriodToast(pType);
                        }}
                        color="#fff"
                        title={
                          <PayQuestionTip
                            type={
                              {
                                1: 2,
                                2: 3,
                                3: 7,
                              }[pType] || 1
                            }
                          />
                        }
                        placement="bottomLeft"
                      />
                    </div>
                  ))}
                </div>
              </SectionRow>
              {isPrePay && (
                <>
                  <SectionRow title="预付设置:">
                    {/* 1: 一次性支付 2: 分期支付 */}
                    {previewObj.config.prePaymentConfig.downPaymentType === 1 ? (
                      <div>
                        下单时按订单金额 {previewObj.config.prePaymentConfig.prePaymentRatio}%
                        预付，完成交易时按收货金额结清
                      </div>
                    ) : (
                      <div>
                        <span>分期支付</span>
                        {previewObj.config.prePaymentConfig.downInstallmentList?.map(
                          (item: any, idx: any) => (
                            <div key={idx}>
                              第{idx + 1}期付款：下单时按照金额的{item.ratio}%进行预付。
                            </div>
                          ),
                        )}
                      </div>
                    )}
                  </SectionRow>
                  {isFeatureActive(FeatureEnum.Agreement) && (
                    <SectionRow title="尾款结清:">
                      {findEnumLabel(
                        afterPaymentConfigPaymentMode,
                        previewObj.config.prePaymentConfig.finalPaymentType,
                      )}
                      {isPrePayMoreTimes && (
                        <div>
                          {previewObj.config.prePaymentConfig.installmentList.map((item, idx) => (
                            <div key={idx}>
                              第 {idx + 1} 期付款：订单完成后 T + {item.deferredDays}
                              日(内)，支付交易金额至 {item.ratio}%。
                              {item.notFinancialPay && (
                                <span className="installment_item_red">
                                  （该笔为非供应链金融支付，不占用额度）
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {isPrePaysettlement ? (
                        <div>
                          <div>
                            结算支付({getPrePaysettlement(previewObj.config.prePaymentConfig)})
                          </div>
                          <div>
                            {getPaymentOrder(
                              previewObj.config.prePaymentConfig.finalSettlementConfig
                                .billingPeriodEnumList,
                              billingPeriodTypes,
                              previewObj.config.prePaymentConfig,
                            )}
                          </div>
                        </div>
                      ) : null}
                    </SectionRow>
                  )}
                  {previewObj.config.payMode ? (
                    <SectionRow title="支付方式:">
                      <span>{previewObj.config.payMode}</span>
                    </SectionRow>
                  ) : null}
                </>
              )}
              {isGoodsFirst && (
                <SectionRow title="货到付款:">
                  {findEnumLabel(
                    afterPaymentConfigPaymentMode,
                    previewObj.config.afterPaymentConfig.paymentModeEnum,
                  )}
                  {isGoodsFirstMoreTimes && (
                    <div>
                      {previewObj.config.afterPaymentConfig.installmentList.map((item, idx) => (
                        <div key={idx}>
                          第 {idx + 1} 期付款：订单完成后 T + {item.deferredDays}
                          日(内)，支付交易金额至 {item.ratio}%
                          {item.notFinancialPay && (
                            <span className="installment_item_red">
                              （该笔为非供应链金融支付，不占用额度）
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </SectionRow>
              )}
              {isCustomPay && (
                <SectionRow title="结算周期:">
                  <div>
                    {findEnumLabel(
                      settlementIntervalEnum,
                      previewObj.config.settlementConfig.settlementIntervalEnum,
                    )}
                    <span>
                      {previewObj.config.settlementConfig.settlementIntervalEnum === 1 &&
                        `T日收货后，T + ${previewObj.config.settlementConfig.settlementAfterDays}日结算。`}
                      {previewObj.config.settlementConfig.settlementIntervalEnum === 2 &&
                        `
                      每周固定结算日为
                      ${findEnumLabel(
                        weekData,
                        previewObj.config.settlementConfig.settlementWeekDays,
                      )}
                      `}
                      {previewObj.config.settlementConfig.settlementIntervalEnum === 3 &&
                        `
                        交易周期为
                        ${previewObj.config.settlementConfig.settlementMonthType},
                        每月固定结算日为
                        ${previewObj.config.settlementConfig.settlementMonthDays}日
                        `}
                    </span>
                  </div>
                </SectionRow>
              )}
              {isCustomPay && (
                <SectionRow title="付款账期:">
                  {getPayPeriodTextNew()}
                  {showSettlementInstallment &&
                    previewObj.config.settlementConfig.installmentList.map((item, idx) => (
                      <div key={idx}>
                        第 {idx + 1} 期付款：结算后 T + {item.deferredDays}
                        日(内)，支付结算金额至 {item.ratio}%
                        {item.notFinancialPay && (
                          <span className="installment_item_red">
                            （该笔为非供应链金融支付，不占用额度）
                          </span>
                        )}
                      </div>
                    ))}
                </SectionRow>
              )}
            </Section>
            <Section title="价格设置">
              <SectionRow title="定价模板:">
                <div>{findEnumLabel(pricingModelEnum, previewObj.config.pricingModelEnum)}</div>
              </SectionRow>
              {isBaseOnSellPrice && (
                <>
                  <SectionRow title="报价类型:">
                    <div>
                      {findEnumLabel(
                        templatePriceConfigQuoteMode,
                        previewObj.config?.baseOnSellPrice?.quoteModeEnum,
                      )}
                      <QuestionIcon
                        onClick={() => {
                          showToast(
                            previewObj.config.baseOnSellPrice &&
                              previewObj.config.baseOnSellPrice.quoteModeEnum === 2
                              ? '含税单计价\n综合单价 = 含税单价\n小计 = 综合单价 * 数量'
                              : '除税价计价\n综合单价 = 除税单价 *（1 + 税率）\n小计 = 除税单价*（1 + 税率） * 数量',
                          );
                        }}
                        title={
                          previewObj.config.baseOnSellPrice &&
                          previewObj.config.baseOnSellPrice.quoteModeEnum === 2 ? (
                            <>
                              <div>含税单计价</div>
                              <div>综合单价 = 含税单价</div>
                              <div>小计 = 综合单价 * 数量</div>
                            </>
                          ) : (
                            <>
                              <div>除税价计价</div>
                              <div>综合单价 = 除税单价 *（1 + 税率）</div>
                              <div>小计 = 除税单价*（1 + 税率） * 数量</div>
                            </>
                          )
                        }
                      />
                      ，商品开票税率为 {previewObj.config.baseOnSellPrice.taxRate}%
                    </div>
                  </SectionRow>
                  <SectionRow title="浮动比例设置:" layout="vertical">
                    <div>
                      <SimpleTable
                        tabledata={previewObj?.config?.baseOnSellPrice?.discountByMethodList ?? []}
                        renderinstruction={tradeModePriceRenderInstruct}
                        onClickTip={onClickTableTip}
                      />
                    </div>
                  </SectionRow>
                </>
              )}
              {isBasePriceConfig && (
                <>
                  <SectionRow title="报价类型:">
                    <div>
                      {findEnumLabel(
                        templatePriceConfigQuoteMode,
                        previewObj.config?.basePriceConfig?.quoteModeEnum,
                      )}
                      <QuestionIcon
                        onClick={() => {
                          showToast(
                            previewObj.config.basePriceConfig &&
                              previewObj.config.basePriceConfig.quoteModeEnum === 2
                              ? '含税单计价\n综合单价 = 含税单价\n小计 = 综合单价 * 数量'
                              : '除税价计价\n综合单价 = 除税单价 *（1 + 税率）\n小计 = 除税单价*（1 + 税率） * 数量',
                          );
                        }}
                        title={
                          previewObj.config.basePriceConfig &&
                          previewObj.config.basePriceConfig.quoteModeEnum === 2 ? (
                            <>
                              <div>含税单计价</div>
                              <div>综合单价 = 含税单价</div>
                              <div>小计 = 综合单价 * 数量</div>
                            </>
                          ) : (
                            <>
                              <div>除税价计价</div>
                              <div>综合单价 = 除税单价 *（1 + 税率）</div>
                              <div>小计 = 除税单价*（1 + 税率） * 数量</div>
                            </>
                          )
                        }
                      />
                      ，商品开票税率为 {previewObj.config.basePriceConfig.taxRate}%
                    </div>
                  </SectionRow>
                  <SectionRow title="浮动比例设置:" layout="vertical">
                    <div>
                      <SimpleTable
                        tabledata={previewObj?.config?.basePriceConfig?.discountByMethodList ?? []}
                        renderinstruction={tradeModePriceRenderInstruct}
                        onClickTip={onClickTableTip}
                      />
                    </div>
                  </SectionRow>
                </>
              )}
              {isSteelBarNetPrice && (
                <>
                  <SectionRow title="报价类型:">
                    <div>
                      {findEnumLabel(
                        templatePriceConfigQuoteMode,
                        previewObj.config.rebarPriceConfig.quoteModeEnum,
                      )}
                      <QuestionIcon
                        onClick={() => {
                          showToast(
                            previewObj.config.rebarPriceConfig.quoteModeEnum === 2
                              ? '含税单计价\n综合单价 = 含税单价\n小计 = 综合单价 * 数量'
                              : '除税价计价\n综合单价 = 除税单价 *（1 + 税率）\n小计 = 除税单价*（1 + 税率） * 数量',
                          );
                        }}
                        title={(previewObj.config.rebarPriceConfig.quoteModeEnum === 2
                          ? '含税单计价\n综合单价 = 含税单价\n小计 = 综合单价 * 数量'
                          : '除税价计价\n综合单价 = 除税单价 *（1 + 税率）\n小计 = 除税单价*（1 + 税率） * 数量'
                        )
                          .split('\n')
                          .map((t, i) => (
                            <div key={i}>{t}</div>
                          ))}
                      />
                      ，商品开票税率为
                      {previewObj.config.rebarPriceConfig.taxRate}%
                    </div>
                  </SectionRow>
                  {netPriceType && (
                    <SectionRow title="网价来源:">
                      <div>{netPriceType === 2 ? '兰格网' : '我的钢铁网'}</div>
                    </SectionRow>
                  )}
                  <SectionRow title="取价规则:" layout="vertical">
                    <div>
                      <div className="price-row">(1) 以哪个环节的日期为准取定基价</div>
                      <div>
                        {hasAccountPeriod(1) && (
                          <div>
                            • 货到付款方式，取
                            <span>
                              {
                                previewObj.config.rebarPriceConfig.takePriceRule
                                  .afterPaymentNetPriceDate
                              }
                            </span>
                            网价
                          </div>
                        )}
                        {hasAccountPeriod(3) && (
                          <div>
                            • 结算付款方式，取
                            <span>
                              {previewObj.config.rebarPriceConfig.takePriceRule.customNetPriceDate}
                            </span>
                            网价
                          </div>
                        )}
                        {hasAccountPeriod(0) && (
                          <div>
                            • 先款后货方式，取
                            <span>
                              {
                                previewObj.config.rebarPriceConfig.takePriceRule
                                  .beforePaymentNetPriceDate
                              }
                            </span>
                            网价
                          </div>
                        )}
                        {hasAccountPeriod(2) && (
                          <div>
                            • 预付款方式，取
                            <span>
                              {
                                previewObj.config.rebarPriceConfig.takePriceRule
                                  .prePaymentNetPriceDate
                              }
                            </span>
                            网价
                          </div>
                        )}
                      </div>
                      <div className="price-row">
                        (2) 取当天哪一条网价
                        <QuestionIcon
                          onClick={() => {
                            showToast('均以当天提交单据时已发布的网价进行计算');
                          }}
                          title="均以当天提交单据时已发布的网价进行计算"
                        />
                      </div>
                      <div>
                        {previewObj.config.rebarPriceConfig.takePriceRule.priceSelect || '--'}
                      </div>
                      <div className="price-row">(3) 节当天取价时暂未发布网价规则</div>
                      <div>• 取前1个工作日的网价</div>
                      <div className="price-row">(4) 节假日，周末取价规则</div>
                      <div>
                        •
                        周末取周五的网价，节假日取放假前一天同城市，同品牌，同规格型号网价，如遇到周末，顺延至周五
                      </div>
                      <div className="price-row">(5) 规格型号锚定规则</div>
                      <div className="agreement-anchor-rules">
                        {previewObj.config.rebarPriceConfig.takePriceRule?.anchoredBreedMaterialQuantityModelConfigList?.map(
                          (row, index) => (
                            <SimpleTable
                              key={row.breedAnchor.breedCode + index}
                              tabledata={[row]}
                              renderinstruction={anchorRulesRenderInstructFn(row)}
                            />
                          ),
                        )}
                      </div>
                      <div className="price-row">(6) 品牌锚定规则</div>
                      <SimpleTable
                        tabledata={
                          previewObj.config.rebarPriceConfig.takePriceRule
                            ?.anchoredBrandConfigList || []
                        }
                        renderinstruction={anchoredBrandConfigListRenderInstruct(
                          previewObj.config.rebarPriceConfig.netPriceSource,
                        )}
                      />
                      <div className="price-row">7 品牌无网价规则</div>
                      <div>
                        {previewObj.config.rebarPriceConfig.takePriceRule.netPriceStandByRule}
                        {previewObj.config.rebarPriceConfig.takePriceRule
                          .netPriceStandByRuleEnum === 4 && (
                          <SimpleTable
                            tabledata={
                              previewObj.config.rebarPriceConfig.takePriceRule
                                ?.multiBrandAverageConfig?.thirdBrandNameList || []
                            }
                            renderinstruction={thirdBrandListRenderInstruct(
                              previewObj.config.rebarPriceConfig.netPriceSource,
                            )}
                          />
                        )}
                        {previewObj.config.rebarPriceConfig.takePriceRule
                          .netPriceStandByRuleEnum === 5 && (
                          <SimpleTable
                            tabledata={
                              previewObj.config.rebarPriceConfig.takePriceRule
                                ?.brandToThirdBrandConfig?.ruleList || []
                            }
                            renderinstruction={ruleListRenderInstruct(
                              previewObj.config.rebarPriceConfig.netPriceSource,
                            )}
                          />
                        )}
                        {previewObj.config.rebarPriceConfig.takePriceRule
                          .netPriceStandByRuleEnum === 6 && (
                          <SimpleTable
                            tabledata={
                              previewObj.config.rebarPriceConfig.takePriceRule
                                ?.thirdBrandDifferenceConfig?.priceParamList || []
                            }
                            renderinstruction={priceParamListRenderInstruct(
                              previewObj.config.rebarPriceConfig.netPriceSource,
                            )}
                          />
                        )}
                      </div>
                      <div className="price-row">
                        8 城市取价规则，默认取本城市网价，如设定锚定城市，则取锚定城市网价
                      </div>
                      <div>
                        <SimpleTable
                          tabledata={
                            previewObj.config.rebarPriceConfig.takePriceRule?.anchoredCityConfig
                              ?.configList || []
                          }
                          renderinstruction={anchoredCityListRenderInstruct}
                        />
                      </div>
                      <div className="price-row">9 不满足上述情况，采购方手动输入单价</div>
                      <div className="float">
                        <div className="float-rule">
                          <p className="float-rule-label">浮动规则:</p>
                          <div className="float-rule-value">
                            <p className="float-rule-val">
                              {findEnumLabel(
                                areaDiscountRuleEnum,
                                previewObj.config.rebarPriceConfig.areaDiscountRuleEnum,
                              )}
                            </p>
                            {previewObj.config.rebarPriceConfig.areaDiscountRuleEnum ===
                              areaDiscountRuleConstant.BY_AREA && (
                              <p
                                className="float-rule-template"
                                onClick={() =>
                                  router.push(
                                    `/area-template/detail?id=${previewObj.config.rebarPriceConfig.areaTemplateSysNo}`,
                                  )
                                }
                              >
                                {previewObj.config.rebarPriceConfig.areaTemplateName}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="price-table">
                          <div>• 按类别浮动</div>
                          {previewObj.config.rebarPriceConfig.areaDiscountRuleEnum ===
                          areaDiscountRuleConstant.BY_AREA ? (
                            <AreaTemplateTable
                              data={previewObj.config.rebarPriceConfig.categoryDiscountList}
                              sysNo={previewObj.config.rebarPriceConfig.areaTemplateSysNo}
                            />
                          ) : (
                            <SimpleTable
                              width="600px"
                              tabledata={
                                previewObj.config.rebarPriceConfig.categoryDiscountList || []
                              }
                              renderinstruction={categoryDiscountListRenderInstruct}
                            />
                          )}
                        </div>
                        <div className="price-table">
                          <div>• 按规格型号浮动</div>
                          <SimpleTable
                            width="670px"
                            tabledata={previewObj.config.rebarPriceConfig.modelDiscountList || []}
                            renderinstruction={modelDiscountListRenderInstruct}
                          />
                        </div>
                        <div className="price-table">
                          <div>• 按品牌浮动</div>
                          <SimpleTable
                            width="670px"
                            tabledata={previewObj.config.rebarPriceConfig.brandDiscountList || []}
                            renderinstruction={brandDiscountListRenderInstruct}
                          />
                        </div>
                        <div className="price-table">
                          <div>• 卸车</div>
                          <SimpleTable
                            width="100%"
                            tabledata={
                              previewObj.config.rebarPriceConfig.unloadingDiscountList || []
                            }
                            renderinstruction={unloadingDiscountListRenderInstruct}
                          />
                        </div>
                        <div className="price-table">
                          <div>• 运距</div>
                          <SimpleTable
                            width="100%"
                            tabledata={[
                              previewObj.config.rebarPriceConfig.transportDistanceDiscount,
                            ]}
                            renderinstruction={transportDistanceDiscountRenderInstruct}
                          />
                        </div>
                      </div>
                    </div>
                  </SectionRow>
                  {previewObj.config.rebarPriceConfig.discountByMethods && (
                    <SectionRow title="按交易方式浮动调价" layout="vertical">
                      <SimpleTable
                        tabledata={previewObj.config.rebarPriceConfig.discountByMethodList}
                        renderinstruction={tradeModePriceRenderInstructConcrete}
                        onClickTip={onClickTableTip}
                      />
                    </SectionRow>
                  )}
                </>
              )}
              {isConcrete && (
                <>
                  <SectionRow title="报价类型:">
                    <div>
                      {findEnumLabel(
                        templatePriceConfigQuoteMode,
                        previewObj.config?.templatePriceConfig?.quoteModeEnum,
                      )}
                      <QuestionIcon
                        onClick={() => {
                          showToast(
                            previewObj.config.templatePriceConfig.quoteModeEnum === 2
                              ? '含税单计价\n综合单价 = 含税单价\n小计 = 综合单价 * 数量'
                              : '除税价计价\n综合单价 = 除税单价 *（1 + 税率）\n小计 = 除税单价*（1 + 税率） * 数量',
                          );
                        }}
                        title={(previewObj.config.templatePriceConfig.quoteModeEnum === 2
                          ? '含税单计价\n综合单价 = 含税单价\n小计 = 综合单价 * 数量'
                          : '除税价计价\n综合单价 = 除税单价 *（1 + 税率）\n小计 = 除税单价*（1 + 税率） * 数量'
                        )
                          .split('\n')
                          .map((t, i) => (
                            <div key={i}>{t}</div>
                          ))}
                      />
                      ，商品开票税率为
                      {previewObj.config.templatePriceConfig.taxRate}%， 报价单位为
                      <span className="square-meter">元/立方米</span>
                    </div>
                  </SectionRow>
                  {previewObj?.config?.templatePriceConfig?.mixingStationConfigList?.length && (
                    <SectionRow title="适用搅拌站:" layout="vertical">
                      <SimpleTable
                        tabledata={previewObj.config.templatePriceConfig.mixingStationConfigList}
                        renderinstruction={mixingStationRenderInstruct}
                        onClickTip={onClickTableTip}
                      />
                    </SectionRow>
                  )}

                  {/* 普通混凝土基价选择 */}
                  <SectionRow title="基准材料品列" layout="vertical" bold>
                    <SimpleTable
                      tabledata={previewObj?.config?.templatePriceConfig?.benchmarkEnumList || []}
                      renderinstruction={baseConcreteRenderInstructfunction}
                      onClickTip={onClickTableTip}
                    />
                  </SectionRow>

                  {/* 普通混凝土价格 1个 */}
                  <SectionRow
                    title="普通混凝土价格"
                    layout="vertical"
                    subTitle="在信息价的基础上浮动"
                    bold
                  >
                    <SimpleTable
                      width="650px"
                      tabledata={
                        Object.values(
                          groupBy(
                            (x: any) => x.attributeSysNo,
                            previewObj.config?.templatePriceConfig?.normalAttributeConfigList || [],
                          ),
                        )?.[0] || []
                      }
                      renderinstruction={normalRenderinstruction}
                      onClickTip={onClickTableTip}
                    />
                  </SectionRow>

                  {/* 泵送混凝土价格 1个 */}
                  {hasBengsong && (
                    <SectionRow
                      title="泵送混凝土价格"
                      layout="vertical"
                      subTitle="在信息价的基础上浮动"
                      bold
                    >
                      <SimpleTable
                        width="650px"
                        tabledata={bengsongValues}
                        renderinstruction={bengsongRenderinstruction}
                        onClickTip={onClickTableTip}
                      />
                    </SectionRow>
                  )}
                  {/* 透水混凝土价格 1个 */}
                  {hasToushui && (
                    <SectionRow
                      title="透水混凝土价格"
                      layout="vertical"
                      subTitle="在信息价的基础上浮动"
                      bold
                    >
                      <SimpleTable
                        width="650px"
                        tabledata={toushuiValues}
                        renderinstruction={toushuiRenderinstruction}
                        onClickTip={onClickTableTip}
                      />
                    </SectionRow>
                  )}

                  {/* 抗折价格 */}
                  <SectionRow
                    title="抗折价格"
                    layout="vertical"
                    subTitle="在普通混凝土价的基础上加/减价"
                    bold
                  >
                    <SimpleTable
                      width="670px"
                      tabledata={doubleAttributeConfigListData}
                      renderinstruction={doubleAttributeConfigListRenderInstruct}
                      onClickTip={onClickTableTip}
                    />
                  </SectionRow>

                  {/* 厂站提供泵价格 */}
                  <SectionRow
                    title="厂站提供泵价格"
                    layout="vertical"
                    subTitle="在同标号普通混凝土价的基础上加/减价"
                    bold
                  >
                    <SimpleTable
                      tabledata={mixStationProvidePumpAttributeConfigListData}
                      renderinstruction={mixStationProvidePumpAttributeConfigListRenderInstruct}
                      onClickTip={onClickTableTip}
                    />
                  </SectionRow>

                  {/* 泵送价格 */}
                  <SectionRow
                    title="泵送价格"
                    layout="vertical"
                    subTitle="在同标号普通混凝土价的基础上加/减价"
                    bold
                  >
                    <SimpleTable
                      tabledata={singleAttributeConfigListData}
                      renderinstruction={singleAttributeConfigListRenderInstruct}
                      onClickTip={onClickTableTip}
                    />
                  </SectionRow>
                  {/* 普通混凝土特殊类型 */}
                  <SectionRow
                    title="普通混凝土特殊类型"
                    layout="vertical"
                    subTitle="在同标号普通混凝土价的基础上加/减价"
                    bold
                  >
                    <SimpleTable
                      tabledata={otherAttributeConfigListData}
                      renderinstruction={otherAttributeConfigListRenderInstruct}
                      onClickTip={onClickTableTip}
                    />
                  </SectionRow>
                  {/* 抗折混凝土特殊类型 */}
                  <SectionRow
                    title="抗折混凝土特殊类型"
                    layout="vertical"
                    subTitle="在同标号抗折混凝土的基础上加/减价"
                    bold
                  >
                    <SimpleTable
                      tabledata={fractureResistanceOtherAttributeConfigListData}
                      renderinstruction={fractureResistanceOtherAttributeConfigListRenderInstruct}
                      onClickTip={onClickTableTip}
                    />
                  </SectionRow>
                  {
                    // 按交易方式浮动调价
                    previewObj.config.templatePriceConfig.discountByMethods && (
                      <SectionRow title="按交易方式浮动调价" layout="vertical" bold>
                        <SimpleTable
                          tabledata={previewObj.config.templatePriceConfig.discountByMethodList}
                          renderinstruction={tradeModePriceRenderInstructConcrete}
                          onClickTip={onClickTableTip}
                        />
                      </SectionRow>
                    )
                  }
                </>
              )}
              {isConcreteCustomize && (
                <>
                  <SectionRow title="报价类型:">
                    <div>
                      {findEnumLabel(
                        templatePriceConfigQuoteMode,
                        previewObj.config.concreteCustomize.quoteModeEnum,
                      )}
                      <QuestionIcon
                        onClick={() => {
                          showToast(
                            previewObj.config.concreteCustomize.quoteModeEnum === 2
                              ? '含税单计价\n综合单价 = 含税单价\n小计 = 综合单价 * 数量'
                              : '除税价计价\n综合单价 = 除税单价 *（1 + 税率）\n小计 = 除税单价*（1 + 税率） * 数量',
                          );
                        }}
                        title={(previewObj.config.concreteCustomize.quoteModeEnum === 2
                          ? '含税单计价\n综合单价 = 含税单价\n小计 = 综合单价 * 数量'
                          : '除税价计价\n综合单价 = 除税单价 *（1 + 税率）\n小计 = 除税单价*（1 + 税率） * 数量'
                        )
                          .split('\n')
                          .map((t, i) => (
                            <div key={i}>{t}</div>
                          ))}
                      />
                      ，商品开票税率为
                      {previewObj.config.concreteCustomize.taxRate}
                      %， 报价单位为
                      <span className="square-meter">元/立方米</span>
                    </div>
                  </SectionRow>
                  {previewObj.config.concreteCustomize.mixingStationConfigList &&
                    previewObj.config.concreteCustomize.mixingStationConfigList.length && (
                      <SectionRow title="适用搅拌站:" layout="vertical">
                        <SimpleTable
                          tabledata={previewObj.config.concreteCustomize.mixingStationConfigList}
                          renderinstruction={mixingStationRenderInstruct}
                          onClickTip={onClickTableTip}
                        />
                      </SectionRow>
                    )}
                  {/* 普通混凝土价格 1个  */}
                  <SectionRow
                    title="普通混凝土价格"
                    layout="vertical"
                    subTitle="在信息价的基础上浮动"
                    bold
                  >
                    <SimpleTable
                      width="100%"
                      tabledata={
                        Object.values(
                          groupBy(
                            (x: any) => x.attributeSysNo,
                            previewObj.config?.concreteCustomize?.customizeBasisConfigList || [],
                          ),
                        )?.[0] || []
                      }
                      renderinstruction={normalCustomizeRenderinstruction}
                      onClickTip={onClickTableTip}
                    />
                  </SectionRow>
                  {/* 抗折价格 */}
                  <SectionRow
                    title="抗折价格"
                    layout="vertical"
                    subTitle="在普通混凝土价的基础上加/减价"
                    bold
                  >
                    <SimpleTable
                      width="670px"
                      tabledata={doubleAttributeConfigListData}
                      renderinstruction={doubleAttributeConfigListRenderInstruct}
                      onClickTip={onClickTableTip}
                    />
                  </SectionRow>
                  {/* 厂站提供泵价格 */}
                  <SectionRow
                    title="厂站提供泵价格"
                    layout="vertical"
                    subTitle="在同标号普通混凝土价的基础上加/减价"
                    bold
                  >
                    <SimpleTable
                      tabledata={mixStationProvidePumpAttributeConfigListData}
                      renderinstruction={mixStationProvidePumpAttributeConfigListRenderInstruct}
                      onClickTip={onClickTableTip}
                    />
                  </SectionRow>
                  {/* 泵送价格 */}
                  <SectionRow
                    title="泵送价格"
                    layout="vertical"
                    subTitle="在同标号普通混凝土价的基础上加/减价"
                    bold
                  >
                    <SimpleTable
                      tabledata={singleAttributeConfigListData}
                      renderinstruction={singleAttributeConfigListRenderInstruct}
                      onClickTip={onClickTableTip}
                    />
                  </SectionRow>
                  {/* 普通混凝土特殊类型 */}
                  <SectionRow
                    title="普通混凝土特殊类型"
                    layout="vertical"
                    subTitle="在同标号普通混凝土价的基础上加/减价"
                    bold
                  >
                    <SimpleTable
                      tabledata={otherAttributeConfigListData}
                      renderinstruction={otherAttributeConfigListRenderInstruct}
                      onClickTip={onClickTableTip}
                    />
                  </SectionRow>
                  {/* 抗折混凝土特殊类型 */}
                  <SectionRow
                    title="抗折混凝土特殊类型"
                    layout="vertical"
                    subTitle="在同标号抗折混凝土的基础上加/减价"
                    bold
                  >
                    <SimpleTable
                      tabledata={fractureResistanceOtherAttributeConfigListData}
                      renderinstruction={fractureResistanceOtherAttributeConfigListRenderInstruct}
                      onClickTip={onClickTableTip}
                    />
                  </SectionRow>
                  {
                    // 按交易方式浮动调价
                    previewObj.config.concreteCustomize.discountByMethods && (
                      <SectionRow title="按交易方式浮动调价" layout="vertical" bold>
                        <SimpleTable
                          tabledata={previewObj.config.concreteCustomize.discountByMethodList}
                          renderinstruction={tradeModePriceRenderInstructConcrete}
                          onClickTip={onClickTableTip}
                        />
                      </SectionRow>
                    )
                  }
                </>
              )}
              {isRenovation && (
                <AgreementRenovationPrice
                  previewObj={previewObj}
                  tradeModePriceRenderInstructConcrete={tradeModePriceRenderInstructConcrete}
                  showToast={showToast}
                  hasAccountPeriod={hasAccountPeriod}
                  onClickTableTip={onClickTableTip}
                />
              )}
              {isAluminum && (
                <>
                  <SectionRow title="报价类型:">
                    <div>
                      {findEnumLabel(templatePriceConfigQuoteMode, 2)}
                      <QuestionIcon
                        onClick={() => {
                          showToast('含税单计价\n综合单价 = 含税单价\n小计 = 综合单价 * 数量');
                        }}
                        title={'含税单计价\n综合单价 = 含税单价\n小计 = 综合单价 * 数量'
                          .split('\n')
                          .map((t, i) => (
                            <div key={i}>{t}</div>
                          ))}
                      />
                      ，商品开票税率为
                      {previewObj.config.aluminumConfig.taxRate}%
                    </div>
                  </SectionRow>
                  <SectionRow title="网价来源:">
                    <div>{previewObj.config.aluminumConfig.netPriceSource}</div>
                  </SectionRow>
                  <SectionRow title="取价规则:" layout="vertical">
                    <div>
                      <div className="price-row">(1) 以哪个环节的日期为准取定基价</div>
                      <div>
                        {hasAccountPeriod(1) && (
                          <div>
                            • 货到付款方式，取
                            <span>
                              {
                                previewObj.config.aluminumConfig.takePriceRule
                                  .afterPaymentNetPriceDate
                              }
                            </span>
                            网价
                          </div>
                        )}
                        {hasAccountPeriod(3) && (
                          <div>
                            • 结算付款方式，取
                            <span>
                              {previewObj.config.aluminumConfig.takePriceRule.customNetPriceDate}
                            </span>
                            网价
                          </div>
                        )}
                        {hasAccountPeriod(0) && (
                          <div>
                            • 先款后货方式，取
                            <span>
                              {
                                previewObj.config.aluminumConfig.takePriceRule
                                  .beforePaymentNetPriceDate
                              }
                            </span>
                            网价
                          </div>
                        )}
                        {hasAccountPeriod(2) && (
                          <div>
                            • 预付款方式，取
                            <span>
                              {
                                previewObj.config.aluminumConfig.takePriceRule
                                  .prePaymentNetPriceDate
                              }
                            </span>
                            网价
                          </div>
                        )}
                      </div>
                      <div className="price-row">(2) 当天取价时暂未发布网价规则</div>
                      <div>• 取前1个工作日的网价</div>
                      <div className="price-row">(3) 节假日，周末取价规则</div>
                      <div>
                        •
                        周末取周五的网价，节假日取放假前一天同城市，同品牌，同规格型号网价，如遇到周末，顺延至周五
                      </div>
                      <div className="price-row">(4) 不满足上述情况，采购方手动输入单价</div>
                      <div className="float">
                        <div className="float-rule">
                          <p className="float-rule-label">浮动规则:</p>
                          <div className="float-rule-value">
                            <p className="float-rule-val">
                              {findEnumLabel(
                                areaDiscountRuleEnum,
                                previewObj.config.aluminumConfig.areaDiscountRuleEnum,
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="price-table">
                          <div>• 基准规格</div>
                          <SimpleTable
                            width="600px"
                            tabledata={
                              Array.isArray(previewObj.config.aluminumConfig.baseNetPrice)
                                ? previewObj.config.aluminumConfig.baseNetPrice || []
                                : [previewObj.config.aluminumConfig.baseNetPrice]
                            }
                            renderinstruction={breedDiscountListRenderInstruct}
                          />
                        </div>
                        <div className="price-table">
                          <div>• 卸车</div>
                          <SimpleTable
                            width="100%"
                            tabledata={previewObj.config.aluminumConfig.unloadingDiscountList || []}
                            renderinstruction={unloadingDiscountListRenderInstruct}
                          />
                        </div>
                        <div className="price-table">
                          <div>• 运距</div>
                          <SimpleTable
                            width="100%"
                            tabledata={[previewObj.config.aluminumConfig.transportDistanceDiscount]}
                            renderinstruction={transportDistanceDiscountRenderInstruct}
                          />
                        </div>
                      </div>
                    </div>
                  </SectionRow>
                  {previewObj.config.aluminumConfig.discountByMethods && (
                    <SectionRow title="按交易方式浮动调价" layout="vertical">
                      <SimpleTable
                        tabledata={previewObj.config.aluminumConfig.discountByMethodList}
                        renderinstruction={tradeModePriceRenderInstructConcrete}
                        onClickTip={onClickTableTip}
                      />
                    </SectionRow>
                  )}
                </>
              )}
            </Section>
          </div>
        )}
      </div>
      <style jsx>{`
        .agreement-wrap {
          background: #fff;
        }
        .agreement-title {
          font-size: 17px;
          color: #000000;
          text-align: center;
          line-height: 24px;
          font-weight: bold;
          padding: 14px 16px;
        }
        :global(.agreement-tip) {
          text-align: left;
        }
        .agreement-wrap :global(.van-icon-question-o) {
          margin-left: 2px;
        }
        .account-period-div {
          display: flex;
          flex-wrap: wrap;
        }
        .account-period-item {
          flex: 0 0 50%;
        }

        .price-row {
          margin-top: 18px;
          margin-bottom: 10px;
        }

        .price-row:first-child {
          margin-top: 0;
        }

        .price-table {
          margin-top: 18px;
          overflow: auto;
        }

        .price-table :global(.table-container) {
          margin-top: 10px;
        }
        .float {
          margin-top: 10px;
        }
        .float p {
          margin: 0;
        }
        .float-rule {
          display: flex;
        }
        .float-rule-label {
          color: #999;
          width: 81px;
        }
        .float-rule-value {
          display: flex;
        }
        .float .float-rule-template {
          margin-left: 20px;
          font-weight: 500;
          color: var(--primaryColor);
        }
        .agreement-anchor-rules :global(.table-container) {
          overflow: auto;
        }
        .agreement-anchor-rules :global(.rv-tag) {
          margin-right: 4px;
          margin-bottom: 4px;
        }
        .agreement-anchor-rules :global(.rv-tag):last-child {
          margin-right: 0;
        }
        .agreement-anchor-rules :global(.border-left) {
          border-left: 1px solid #000;
        }
        .agreement-anchor-rules :global(.table-container .header) {
          border: none;
        }
        .agreement-anchor-rules :global(.header-column-item) {
          flex: 0 0 auto;
          background: #f6f6f6;
        }
        .agreement-anchor-rules :global(.row-column-item) {
          flex: 0 0 auto;
        }
        .agreement-anchor-rules :global(.table-container .header .header-column-item:last-of-type) {
          flex: 0 0 auto;
        }
        .agreement-anchor-rules
          :global(.table-container .row-item-container .row-column-item:last-of-type) {
          flex: 0 0 auto;
        }
        .installment_item_red {
          color: #ff3115;
        }
      `}</style>
    </>
  );
}

const Section = ({ title, children = null }) => (
  <>
    <div className="section">
      <div className="title">{title}</div>
      {children}
    </div>
    <style jsx>{`
      .title {
        background: #f2f2f2;
        font-size: 16px;
        color: #000000;
        font-weight: bold;
        height: 48px;
        line-height: 48px;
        padding-left: 16px;
      }

      .section :global(.section-row.horizontal):last-child {
        border: 0;
      }
    `}</style>
  </>
);

export const SectionRow = ({
  title,
  children = null,
  bordered = true,
  layout = 'horizontal',
  subTitle = '',
  bold = false,
  titleWidth = null,
}) => (
  <>
    <div
      className={cls(
        'section-row',
        bordered && layout == 'horizontal' && 'bordered',
        layout == 'horizontal' && 'horizontal',
        layout == 'vertical' && 'vertical',
      )}
    >
      <div
        className={cls('title', bold && 'bold')}
        style={titleWidth ? { flex: `0 0 ${titleWidth}` } : undefined}
      >
        {title}
        {subTitle && <span>{subTitle}</span>}
      </div>
      <div className="content">{children}</div>
    </div>
    <style jsx>{`
      .section-row {
        padding: 19px 0;
        margin: 0 16px;
        display: flex;
      }
      .section-row.bordered {
        border-bottom: 1px solid #eeeeee;
      }
      .title {
        font-size: 13px;
        color: #999999;
      }
      .title.bold {
        font-size: 14px;
        color: #000000;
        font-weight: bold;
      }
      .title span {
        transform: scale(0.85);
        font-weight: 400;
        padding-left: 8px;
        color: #999999;
      }
      .horizontal .title {
        flex: 0 0 81px;
      }
      .content {
        width: 100%;
        font-size: 13px;
      }
      .vertical {
        flex-direction: column;
      }
      .vertical .content {
        margin-top: 18px;
        overflow: auto;
      }
    `}</style>
  </>
);
